<template>
  <div class="FormLaptop">
    <FormSect>
      <RowGrid :columns="2">
        <FieldLaptopImage
          v-if="laptop.id"
          :imageFileId.sync="laptop.imageFileId"
          :laptopId="laptop.id" />
        <div>
          <Field
            data-cy="name"
            :label="$t('name')"
            :value.sync="laptop.name" />
          <Field
            data-cy="price"
            :label="$t('price')"
            type="money"
            :value.sync="laptop.price" />
        </div>
      </RowGrid>
      <RowGrid :columns="3">
        <Field
          data-cy="cpu"
          :label="$t('cpu')"
          :value.sync="laptop.cpu" />
        <Field
          data-cy="gpu"
          :label="$t('gpu')"
          :value.sync="laptop.gpu" />
        <Field
          data-cy="weight"
          :label="$t('weight')"
          step="0.001"
          type="number"
          :value.sync="laptop.weight" />
      </RowGrid>
    </FormSect>
    <FormSect :title="$t('memory')">
      <RowGrid :columns="4">
        <Field
          data-cy="memoryAmount"
          :label="$t('memoryAmount')"
          type="number"
          :value.sync="laptop.memoryAmount" />
        <Field
          data-cy="hddAmount"
          :label="$t('hddAmount')"
          type="number"
          :value.sync="laptop.hddAmount" />
        <Field
          data-cy="ssdAmount"
          :label="$t('ssdAmount')"
          type="number"
          :value.sync="laptop.ssdAmount" />
        <Field
          class="nvme"
          data-cy="hasNvme"
          :label="$t('hasNvme')"
          type="checkbox"
          :value.sync="laptop.hasNvme" />
      </RowGrid>
    </FormSect>
    <FormSect :title="$t('screen')">
      <RowGrid :columns="4">
        <Field
          data-cy="screen"
          :label="$t('screen')"
          :value.sync="laptop.screen" />
        <Field
          data-cy="screenHz"
          :label="$t('screenHz')"
          type="number"
          :value.sync="laptop.screenHz" />
        <Field
          data-cy="resolution"
          :label="$t('resolution')"
          :value.sync="laptop.resolution" />
        <Field
          data-cy="screenContrast"
          :label="$t('screenContrast')"
          type="number"
          :value.sync="laptop.screenContrast" />
        <Field
          data-cy="screenNits"
          :label="$t('screenNits')"
          type="number"
          :value.sync="laptop.screenNits" />
        <Field
          data-cy="screenRgb"
          :label="$t('screenRgb')"
          type="number"
          :value.sync="laptop.screenRgb" />
        <Field
          data-cy="screenSrgb"
          :label="$t('screenSrgb')"
          type="number"
          :value.sync="laptop.screenSrgb" />
        <Field
          data-cy="screenP3"
          :label="$t('screenP3')"
          type="number"
          :value.sync="laptop.screenP3" />
      </RowGrid>
    </FormSect>
    <FormSect :title="$t('ports')">
      <RowGrid :columns="6">
        <Field
          data-cy="portsDisplayPort"
          :label="$t('portsDisplayPort')"
          type="number"
          :value.sync="laptop.portsDisplayPort" />
        <Field
          data-cy="portsHdmi"
          :label="$t('portsHdmi')"
          type="number"
          :value.sync="laptop.portsHdmi" />
        <Field
          data-cy="portsThunderbolt"
          :label="$t('portsThunderbolt')"
          type="number"
          :value.sync="laptop.portsThunderbolt" />
        <Field
          data-cy="portsUsb2"
          :label="$t('portsUsb2')"
          type="number"
          :value.sync="laptop.portsUsb2" />
        <Field
          data-cy="portsUsb3a"
          :label="$t('portsUsb3a')"
          type="number"
          :value.sync="laptop.portsUsb3a" />
        <Field
          data-cy="portsUsb3c"
          :label="$t('portsUsb3c')"
          type="number"
          :value.sync="laptop.portsUsb3c" />
      </RowGrid>
    </FormSect>
    <FormSect :title="$t('battery')">
      <RowGrid :columns="3">
        <Field
          data-cy="batteryCells"
          :label="$t('batteryCells')"
          type="number"
          :value.sync="laptop.batteryCells" />
        <Field
          data-cy="batteryWh"
          :label="$t('batteryWh')"
          type="number"
          :value.sync="laptop.batteryWh" />
        <Field
          data-cy="batteryMinutes"
          :label="$t('batteryMinutes')"
          type="number"
          :value.sync="laptop.batteryMinutes" />
      </RowGrid>
    </FormSect>
    <FormSect :title="$t('additionalFeatures')">
      <Row>
        <Column :md="2">
          <Field
            data-cy="hasBacklight"
            :label="$t('hasBacklight')"
            type="checkbox"
            :value.sync="laptop.hasBacklight" />
        </Column>
        <Column :md="2">
          <Field
            data-cy="hasNumpad"
            :label="$t('hasNumpad')"
            type="checkbox"
            :value.sync="laptop.hasNumpad" />
        </Column>
      </Row>
      <FormTagsSelectable
        :label="$t('bonus')"
        :tags="laptop.bonus" />
    </FormSect>
    <FormSect>
      <RowFlex class="constrainButtons">
        <Field
          disabled
          :label="$t('checkedAt')"
          :value="laptop.checkedAt && $d(laptop.checkedAt, 'short')">
          <FieldBtns>
            <FieldBtn
              :isLoading="isSettingCheckedAt"
              @click="setAsChecked(new Date())">
              {{ $t('setAsChecked') }}
            </FieldBtn>
            <FieldBtn
              v-if="laptop.checkedAt"
              class="danger light"
              :isLoading="isSettingCheckedAt"
              @click="setAsChecked(null)">
              <Icon name="cross" />
            </FieldBtn>
          </FieldBtns>
        </Field>
        <a
          v-if="laptop.asin"
          class="Btn ib sm"
          :href="urlAmazon"
          rel="noopener noreferrer"
          target="_blank">
          {{ $t('amazon') }}
        </a>
        <Field
          data-cy="isRefurbished"
          :isSwitch="true"
          :label="$t('isRefurbished')"
          type="checkbox"
          :value.sync="laptop.isRefurbished" />
        <Field
          data-cy="isArchived"
          :isSwitch="true"
          :label="$t('isArchived')"
          type="checkbox"
          :value.sync="laptop.isArchived" />
      </RowFlex>
    </FormSect>
    <FormSect :title="$t('links')">
      <MultiForm
        :items.sync="laptop.linksExternal"
        @add="addLink">
        <template #default="{ item }">
          <RowGrid :columns="4">
            <Field
              :label="$t('link.label')"
              :value.sync="item.label" />
            <Field
              :label="$t('link.url')"
              :value.sync="item.url" />
            <Field
              :label="$t('link.type')"
              :options="linksExternalTypeOptions"
              type="select"
              :value.sync="item.type" />
            <Field
              :label="$t('link.price')"
              :value.sync="item.price" />
            <Field
              :label="$t('link.updatedAt')"
              :value.sync="item.updatedAt" />
          </RowGrid>
        </template>
      </MultiForm>
    </FormSect>
    <FormLaptopReviews
      :laptop="laptop"
      @update:laptop="$up('laptop', $event)" />
  </div>
</template>

<script>
import { actions, getters } from '@utils'
import FieldLaptopImage from './FieldLaptopImage'
import FormLaptopReviews from './FormLaptopReviews'
import FormTagsSelectable from '@forms/FormTagsSelectable'
import Message from '@mixins/Message'
import MultiForm from '@forms/MultiForm'

export default {
  mixins: [Message],
  components: {
    FieldLaptopImage,
    FormLaptopReviews,
    FormTagsSelectable,
    MultiForm,
  },
  props: {
    laptop: Object,
  },
  data() {
    return {
      isCrawling: false,
      isSettingCheckedAt: false,
    }
  },
  computed: {
    urlAmazon() {
      return this.formAmazonLink(this.laptop.asin)
    },
    ...getters(
      'formAmazonLink',
      'linksExternalFactory',
      'linksExternalTypeOptions',
    ),
  },
  methods: {
    addLink() {
      return this.laptop.linksExternal.push(this.linksExternalFactory())
    },
    async setAsChecked(checkedAt) {
      const laptopPatched = await this.$wrap(
        () => this.PATCH_LAPTOP({
          id: this.laptop.id,
          checkedAt,
        }),
        'isSettingCheckedAt',
      )

      this.laptop.checkedAt = laptopPatched.checkedAt
    },
    ...actions(
      'PATCH_LAPTOP',
    ),
  },
}
</script>

<style lang="scss">
.FormLaptop {
  padding: $u;

  .nvme input[type=checkbox] {
    margin-top: 3.5rem;
  }
}
</style>

<i18n>
{
  "en": {
    "additionalFeatures": "Additional features",
    "amazon": "View on Amazon",
    "battery": "Battery",
    "batteryCells": "Battery Cells",
    "batteryMinutes": "Battery Minutes",
    "batteryWh": "Battery Wh",
    "bonus": "Bonus",
    "cpu": "CPU",
    "gpu": "GPU",
    "hasBacklight": "Backlight",
    "hasNumpad": "Numpad",
    "hasNvme": "Nvme",
    "hddAmount": "HDD Amount, GB",
    "isArchived": "Archived",
    "isRefurbished": "Refurbished",
    "links": "Links",
    "link": {
      "label": "Label",
      "price": "Price",
      "updatedAt": "Updated at",
      "url": "URL"
    },
    "memory": "Memory",
    "memoryAmount": "RAM, GB",
    "name": "Name",
    "ports":"Ports",
    "portsDisplayPort": "DisplayPort",
    "portsHdmi": "HDMI",
    "portsThunderbolt":"Thunderbolt",
    "portsUsb2": "USB 2.0",
    "portsUsb3a": "USB 3.x A",
    "portsUsb3c": "USB 3.x C",
    "price": "Price",
    "resolution": "Resolution",
    "checkedAt": "Checked",
    "reviews": "Reviews",
    "screen": "Screen",
    "screenHz": "Screen hertz",
    "screenContrast": "Contrast",
    "screenNits": "Nits",
    "screenP3":"P3",
    "screenRgb": "RGB",
    "screenSrgb":"sRGB",
    "setAsChecked": "Set as checked",
    "ssdAmount": "SSD Amount, GB",
    "weight": "Weight, kg"
  }
}
</i18n>
